import React, { useState, useEffect, useRef, useContext } from "react";
import AppContext from "../context";
import InitModal from "./initModal/index";
import variables from "../styles/export.module.scss";

const GuidedTour = ({ onClose }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [skipNextTime, setSkipNextTime] = useState(false);
  const tooltipRef = useRef(null);

  // Get handleSetGlobalModal from AppContext
  const { handleSetGlobalModal } = useContext(AppContext);

  const steps = [
    {
      target: "#myCanvas",
      content:
        "This is a 3d model of your drawer insert. This will sit inside your drawer when it is made. Inside and outside dimensions are shown by the blue lines and arrows on the model. ",
    },
    {
      target: "#tutorialTemplates",
      content:
        "Click here to access our library of pre-loaded templates, or for a video walkthrough of the Drawer Insert Designer tool.",
    },
    {
      target: "#insertPrice",
      content:
        "This is the current insert price. You can add it to the cart with the blue button.",
    },
    {
      target: "#insertNaming",
      content:
        "You can name your insert here. This is how it will appear on your order and if you save it as a template",
    },
    {
      target: "#dimensions",
      content:
        "Use these drop down menus to change the dimensions of your insert. Remember that your insert should be 1/8” shorter in each dimension than the inside dimenions of the drawer it will sit in.",
    },
    {
      target: "#materials",
      content:
        "Select what material you’d like your insert to be made from. Selecting a new material will update the 3d model.",
    },
    {
      target: "#bottoms",
      content:
        "Select a bottom material for your drawer insert. Felt and non-slip bottoms have multiple colours that can be chosen from the  “Colour” dropdown when an applicable option has been selected. More information about bottom materials can be found under the “more” menu at the top of the tool.",
    },
    {
      target: "#navigation",
      content:
        "Use these sliders change the position of the 3d model. You can also click and drag directly on the 3d model to change the view. The explode slider will show a view of all the parts that make up the insert.",
    },
    {
      target: "#dividerTools",
      content:
        "Select one of these buttons, then click anywhere on the 3d model to place a divider. Full dividers span the entire insert and will intersect existing dividers, while partial dividers stop when they hit an existing divider.",
    },
    {
      target: "#spacingTools",
      content:
        "Select this button, then select every divider you’d like to space evenly, then click “apply” to evenly spread the selected dividers evenly within the insert.",
    },
    {
      target: "#moduleTools",
      content:
        "Select “Knife Block” or “Spice Rack” then click on any green highlighted area to place one. Remember that each item will have a maximum and minimum space required. Select a remove button, then click on a knife block or spice rack to remove it from the insert.",
    },
    {
      target: "#cutOutTools",
      content:
        "Select this button, then select a divider to add a divider cut-out to the selected divider.",
    },
    {
      target: "#removeTools",
      content:
        "Remove a divider cut-out, entire divider, or all placed items by selecting one of the buttons, then selecting the divider cut-out, or divider you’d like to remove. If you click “Delete All” you will be prompted to confirm before everything is removed.",
    },
    {
      target: "#settings",
      content:
        "Here you can toggle “Tooltips” which are the descriptions of the buttons above that appear when you hover your mouse over them. You can also toggle the on-screen dimensions that appear on the 3d model.",
    },
    {
      target: "#fileOptions",
      content:
        "Save and load templates locally to and from your device, or print a picture of the 3d model from this menu.",
    },
    {
      target: "#moreOptions",
      content:
        'Click here for more information about insert materials and bottoms, as well as more about our shipping policies and our contact information for more help. You can also start this guided tour again by clicking the "Guided Tour" button',
    },
  ];

  useEffect(() => {
    // Check if user has opted to skip the tour
    const shouldSkipTour =
      localStorage.getItem("skipDrawerInsertTour") === "true";
    if (shouldSkipTour) {
      handleClose();
      return;
    }

    if (isActive) {
      highlightTarget(steps[currentStepIndex].target);

      // Add slight delay to ensure target is highlighted before positioning tooltip
      setTimeout(() => {
        positionTooltip(steps[currentStepIndex].target);
      }, 50);

      // Add a transparent overlay to prevent interaction with other elements
      addBlockingOverlay();

      // Add resize event listener to update positions when window size changes
      window.addEventListener("resize", handleResize);
    }

    return () => {
      // Remove any highlights when component unmounts or step changes
      removeHighlight();
      // Remove blocking overlay
      removeBlockingOverlay();
      // Clean up resize event listener
      window.removeEventListener("resize", handleResize);
    };
  }, [currentStepIndex, isActive]);

  // Handler for window resize events
  const handleResize = () => {
    if (isActive && steps[currentStepIndex]) {
      // Use setTimeout to debounce rapid resize events
      clearTimeout(window.resizeTimer);
      window.resizeTimer = setTimeout(() => {
        highlightTarget(steps[currentStepIndex].target);
        positionTooltip(steps[currentStepIndex].target);
      }, 100);
    }
  };

  const addBlockingOverlay = () => {
    // Remove any existing blocking overlay first
    removeBlockingOverlay();

    // Create blocking overlay
    const blockingOverlay = document.createElement("div");
    blockingOverlay.className = "tour-blocking-overlay";
    blockingOverlay.style.position = "fixed";
    blockingOverlay.style.top = "0";
    blockingOverlay.style.left = "0";
    blockingOverlay.style.width = "100%";
    blockingOverlay.style.height = "100%";
    blockingOverlay.style.zIndex = "9999"; // Below tooltips but above everything else
    blockingOverlay.style.backgroundColor = "transparent"; // Transparent but blocks clicks

    // Add event listeners to prevent any interactions
    blockingOverlay.addEventListener("click", (e) => e.stopPropagation());
    blockingOverlay.addEventListener("mousedown", (e) => e.stopPropagation());
    blockingOverlay.addEventListener("mouseup", (e) => e.stopPropagation());
    blockingOverlay.addEventListener("dblclick", (e) => e.stopPropagation());

    document.body.appendChild(blockingOverlay);
  };

  const removeBlockingOverlay = () => {
    const overlays = document.querySelectorAll(".tour-blocking-overlay");
    overlays.forEach((el) => el.parentNode.removeChild(el));
  };

  const highlightTarget = (targetSelector) => {
    removeHighlight();

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    let rect;

    // Special case for the first step (3D model of drawer insert)
    if (currentStepIndex === 0 && targetSelector === "#myCanvas") {
      rect = targetElement.getBoundingClientRect();

      // Create a custom highlight element that's 50% width, centered
      const customHighlight = document.createElement("div");
      customHighlight.className = "tour-custom-highlight";
      customHighlight.style.position = "fixed";
      customHighlight.style.width = rect.width * 0.6 + "px"; // 50% of canvas width
      customHighlight.style.height = rect.height + "px";
      customHighlight.style.top = rect.top + "px";
      // Center the highlight in the canvas element
      customHighlight.style.left =
        rect.left + (rect.width - rect.width * 0.59) / 2 + "px";
      customHighlight.style.border = `2px solid ${variables.primaryColor}`;
      customHighlight.style.borderRadius = "4px";
      customHighlight.style.boxShadow = "0 0 0 10000px rgba(0,0,0,0.5)";
      customHighlight.style.zIndex = "10000";
      customHighlight.style.pointerEvents = "none";

      document.body.appendChild(customHighlight);

      return; // Skip the normal highlighting for this special case
    }

    // Normal highlighting for other elements
    targetElement.classList.add("tour-highlight");
    rect = targetElement.getBoundingClientRect();

    const buffer = 5; // Extra space around the element

    // Create spotlight effect with multiple elements instead of using mask
    // This approach has better cross-browser compatibility
    const overlays = [
      // Top overlay
      createOverlayElement(0, 0, "100%", rect.top - buffer + "px"),

      // Right overlay
      createOverlayElement(
        rect.right + buffer + "px",
        rect.top - buffer + "px",
        window.innerWidth - rect.right - buffer + "px",
        rect.height + buffer * 2 + "px"
      ),

      // Bottom overlay
      createOverlayElement(
        0,
        rect.bottom + buffer + "px",
        "100%",
        window.innerHeight - rect.bottom - buffer + "px"
      ),

      // Left overlay
      createOverlayElement(
        0,
        rect.top - buffer + "px",
        rect.left - buffer + "px",
        rect.height + buffer * 2 + "px"
      ),
    ];

    // Add all overlay elements to the body
    overlays.forEach((overlay) => document.body.appendChild(overlay));
  };

  const createOverlayElement = (left, top, width, height) => {
    const overlay = document.createElement("div");
    overlay.className = "tour-overlay";
    overlay.style.top = top;
    overlay.style.left = left;
    overlay.style.width = width;
    overlay.style.height = height;
    overlay.style.position = "fixed";

    return overlay;
  };

  const removeHighlight = () => {
    // Remove highlight from previous element
    const highlightedElements = document.querySelectorAll(".tour-highlight");
    highlightedElements.forEach((el) => el.classList.remove("tour-highlight"));

    // Remove custom highlight elements
    const customHighlights = document.querySelectorAll(
      ".tour-custom-highlight"
    );
    customHighlights.forEach((el) => el.parentNode.removeChild(el));

    // Remove overlay
    const overlays = document.querySelectorAll(".tour-overlay");
    overlays.forEach((el) => el.parentNode.removeChild(el));
  };

  const positionTooltip = (targetSelector) => {
    if (!tooltipRef.current) return;

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    const targetRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    // Calculate the best position (top, bottom, left, right)
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Determine best placement (top, right, bottom, left)
    const spaceAbove = targetRect.top;
    const spaceBelow = viewportHeight - targetRect.bottom;
    const spaceLeft = targetRect.left;
    const spaceRight = viewportWidth - targetRect.right;

    // Find the side with most space
    const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight);

    let top, left;
    const buffer = 10; // Space between tooltip and target

    // Position based on where there's most space
    if (maxSpace === spaceBelow) {
      // Position below
      top = targetRect.bottom + buffer;
      left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
      tooltipRef.current.setAttribute("data-placement", "bottom");
    } else if (maxSpace === spaceAbove) {
      // Position above
      top = targetRect.top - tooltipRect.height - buffer;
      left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
      tooltipRef.current.setAttribute("data-placement", "top");
    } else if (maxSpace === spaceRight) {
      // Position to the right
      top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
      left = targetRect.right + buffer;
      tooltipRef.current.setAttribute("data-placement", "right");
    } else {
      // Position to the left
      top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
      left = targetRect.left - tooltipRect.width - buffer;
      tooltipRef.current.setAttribute("data-placement", "left");
    }

    // Ensure tooltip stays within viewport
    if (top < 10) top = 10;
    if (left < 10) left = 10;
    if (top + tooltipRect.height > viewportHeight - 10) {
      top = viewportHeight - tooltipRect.height - 10;
    }
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }

    tooltipRef.current.style.top = `${top}px`;
    tooltipRef.current.style.left = `${left}px`;
  };

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Show measurements modal when tour is completed
      handleClose();
      // Use a timeout to ensure the tour is closed before showing the modal
      setTimeout(() => {
        showMeasurementsForm();
      }, 100);
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleSkip = () => {
    handleClose();
    // Use a timeout to ensure the tour is closed before showing the modal
    setTimeout(() => {
      showMeasurementsForm();
    }, 100);
  };

  const handleClose = () => {
    setIsActive(false);
    removeHighlight();
    removeBlockingOverlay(); // Ensure blocking overlay is removed
    if (skipNextTime) {
      localStorage.setItem("skipDrawerInsertTour", "true");
    }
    if (onClose) onClose();
  };

  const handleCheckboxChange = () => {
    setSkipNextTime(!skipNextTime);
  };

  const showMeasurementsForm = () => {
    // Use the global modal system from the app context
    if (handleSetGlobalModal) {
      handleSetGlobalModal(<InitModal />, { semiwide: true });
    }
  };

  if (!isActive) return null;

  return (
    <>
      <div
        className="tour-tooltip"
        ref={tooltipRef}
        style={{
          position: "fixed",
          zIndex: 10001, // Higher than blocking overlay
          background: "white",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          padding: "15px",
          borderRadius: "4px",
          maxWidth: "320px",
          transition: "all 0.3s ease",
          opacity: 0.95,
        }}
      >
        <div className="tour-content">{steps[currentStepIndex].content}</div>
        <div
          className="tour-progress"
          style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}
        >
          Step {currentStepIndex + 1} of {steps.length}
        </div>
        <div
          className="tour-controls"
          style={{
            marginTop: "15px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            <button
              onClick={handlePrev}
              disabled={currentStepIndex === 0}
              style={{
                padding: "8px 12px",
                marginRight: "8px",
                background:
                  currentStepIndex === 0 ? "#ccc" : variables.primaryColor,
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentStepIndex === 0 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <button
              onClick={handleNext}
              style={{
                padding: "8px 12px",
                background: variables.primaryColor,
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {currentStepIndex === steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
          <button
            onClick={handleSkip}
            style={{
              padding: "8px 12px",
              background: "transparent",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Skip
          </button>
        </div>
        <div className="tour-skip-option" style={{ marginTop: "10px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: "14px",
              color: "#555",
            }}
          >
            <input
              type="checkbox"
              checked={skipNextTime}
              onChange={handleCheckboxChange}
              style={{ marginRight: "8px" }}
            />
            Don't show this tour next time
          </label>
        </div>

        {/* Add arrow/indicator based on placement */}
        <div className="tour-tooltip-arrow"></div>
      </div>
    </>
  );
};

export default GuidedTour;
